// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the shared scratchpad of a chat, one per channel. Saves send the version and
// text we started from so the server can merge other people's edits line by line; a
// true conflict (same line changed by both) comes back as 409 and we take the server's
// text, showing the other person's version. Line locks say who is typing where. The
// change log (who added, ticked, edited or removed what) is fetched when someone looks.

import {Endpoints} from '@app/features/app/constants/Endpoints';
import {getProtectedLocalStorage} from '@app/features/platform/state/ProtectedWebStorage';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import Users from '@app/features/user/state/Users';
import {mergeLines} from '@konvo/constants/src/LineMerge';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('KonvoChannelScratchpad');
const SAVE_DEBOUNCE_MS = 500;
const RETRY_MS = 5000;
const LOCK_REFRESH_MS = 2500;
const CHANGES_SEEN_KEY = 'konvo:pad-changes-seen:v1';

export interface ChannelPadLock {
	user_id: string;
	line: string;
	until: number;
}

export interface ChannelPadPayload {
	channel_id: string;
	content: string;
	version: number;
	updated_at: string | null;
	updated_by: string | null;
	locks?: Array<ChannelPadLock>;
	client_id?: string | null;
	merged?: boolean;
	conflict?: boolean;
}

export type ChannelPadStatus = 'idle' | 'loading' | 'saving' | 'saved' | 'offline' | 'conflict';

export type PadChangeItem =
	| {kind: 'added' | 'removed' | 'ticked' | 'unticked'; text: string}
	| {kind: 'edited'; text: string; from: string};

/** One person's editing session: what they added, ticked, edited or removed. */
export interface PadChange {
	user_id: string;
	started_at: string;
	updated_at: string;
	items: Array<PadChangeItem>;
	more: number;
}

/** When this device last looked at each pad's changes (ISO), so new ones get a dot once. */
function readSeen(): Record<string, string> {
	try {
		const value = JSON.parse(getProtectedLocalStorage()?.getItem(CHANGES_SEEN_KEY) ?? '{}') as unknown;
		return value && typeof value === 'object' ? (value as Record<string, string>) : {};
	} catch {
		return {};
	}
}

function writeSeen(seen: Record<string, string>): void {
	try {
		// Only the most recent pads are worth remembering.
		const recent = Object.entries(seen)
			.sort((a, b) => b[1].localeCompare(a[1]))
			.slice(0, 200);
		getProtectedLocalStorage()?.setItem(CHANGES_SEEN_KEY, JSON.stringify(Object.fromEntries(recent)));
	} catch {
		// storage unavailable
	}
}

function randomClientId(): string {
	try {
		return crypto.randomUUID();
	} catch {
		return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
	}
}

export class ChannelPad {
	readonly channelId: string;
	content = '';
	version = 0;
	updatedBy: string | null = null;
	updatedAt: string | null = null;
	status: ChannelPadStatus = 'idle';
	loaded = false;
	dirty = false;
	locks: Array<ChannelPadLock> = [];
	changes: Array<PadChange> | null = null;
	changesStatus: 'idle' | 'loading' | 'failed' = 'idle';
	/** The log is out of date once anyone saves; it is fetched again the next time someone looks. */
	private changesStale = true;
	/** When this device last looked at the changes. */
	seenAt: string | null = null;
	/** When someone other than us last saved, as far as this device has heard. */
	othersSavedAt: string | null = null;
	/** What the current edits are based on; sent with every save so the server can merge. */
	private baseContent = '';
	private baseVersion = 0;
	readonly clientId = randomClientId();
	private saveTimer: ReturnType<typeof setTimeout> | null = null;
	private retryTimer: ReturnType<typeof setTimeout> | null = null;
	private inFlight: Promise<void> | null = null;
	private myLine: string | null = null;
	private lockSentAt = 0;

	constructor(channelId: string) {
		this.channelId = channelId;
		this.seenAt = readSeen()[channelId] ?? null;
		makeAutoObservable<
			this,
			'saveTimer' | 'retryTimer' | 'inFlight' | 'myLine' | 'lockSentAt' | 'baseContent' | 'baseVersion' | 'changesStale'
		>(
			this,
			{
				saveTimer: false,
				retryTimer: false,
				inFlight: false,
				myLine: false,
				lockSentAt: false,
				baseContent: false,
				baseVersion: false,
				changesStale: false,
				clientId: false,
			},
			{autoBind: true},
		);
	}

	get padContent(): string {
		return this.content;
	}

	/** Locks held by other people that have not expired. */
	get liveLocks(): Array<ChannelPadLock> {
		const now = Date.now();
		return this.locks.filter((lock) => lock.until > now);
	}

	/** Someone else saved after this device last looked at the changes. */
	get hasUnseenChanges(): boolean {
		return this.othersSavedAt != null && (!this.seenAt || this.othersSavedAt > this.seenAt);
	}

	markChangesSeen(): void {
		const now = new Date().toISOString();
		// Server and device clocks differ a little; never mark less than what we have heard of.
		this.seenAt = this.othersSavedAt && this.othersSavedAt > now ? this.othersSavedAt : now;
		writeSeen({...readSeen(), [this.channelId]: this.seenAt});
	}

	private noteSaver(payload: ChannelPadPayload): void {
		this.updatedBy = payload.updated_by ?? null;
		this.updatedAt = payload.updated_at ?? null;
		this.changesStale = true;
		if (payload.updated_by && payload.updated_at && payload.updated_by !== Users.currentUserId) {
			if (!this.othersSavedAt || payload.updated_at > this.othersSavedAt) this.othersSavedAt = payload.updated_at;
		}
	}

	async loadChanges(): Promise<void> {
		if (this.changesStatus === 'loading' || (!this.changesStale && this.changes)) return;
		this.changesStale = false;
		this.changesStatus = 'loading';
		try {
			const response = await http.get<{changes?: Array<PadChange>}>(
				Endpoints.KONVO_CHANNEL_SCRATCHPAD_CHANGES(this.channelId),
				{mode: 'silent'},
			);
			if (!response.ok || !Array.isArray(response.body?.changes))
				throw new Error(`changes failed with ${response.status}`);
			const changes = response.body.changes;
			runInAction(() => {
				this.changes = changes;
				this.changesStatus = 'idle';
			});
		} catch (error) {
			logger.warn('Failed to load channel scratchpad changes', error);
			runInAction(() => {
				this.changesStale = true;
				this.changesStatus = 'failed';
			});
		}
	}

	async load(): Promise<void> {
		if (this.dirty) return;
		runInAction(() => {
			if (!this.loaded) this.status = 'loading';
		});
		try {
			const response = await http.get<ChannelPadPayload>(Endpoints.KONVO_CHANNEL_SCRATCHPAD(this.channelId), {
				mode: 'silent',
			});
			// Silent requests do not throw on errors; an error body must not be taken for an empty pad.
			if (!response.ok) throw new Error(`load failed with ${response.status}`);
			const body = response.body;
			if (!body || typeof body.content !== 'string') throw new Error('load returned no pad');
			runInAction(() => {
				if (this.dirty) return;
				this.accept(body);
				this.loaded = true;
				this.status = 'saved';
			});
		} catch (error) {
			logger.warn('Failed to load channel scratchpad', error);
			runInAction(() => {
				this.status = this.loaded ? this.status : 'offline';
			});
		}
	}

	private accept(payload: ChannelPadPayload): void {
		this.content = payload.content ?? '';
		this.version = payload.version ?? 0;
		this.noteSaver(payload);
		this.baseContent = this.content;
		this.baseVersion = this.version;
		if (payload.locks) this.locks = payload.locks;
	}

	setPadContent(next: string): void {
		if (next === this.content) return;
		this.content = next;
		this.dirty = true;
		this.loaded = true;
		this.status = 'saving';
		this.scheduleSave();
	}

	flush(): void {
		if (this.saveTimer != null) {
			clearTimeout(this.saveTimer);
			this.saveTimer = null;
		}
		if (this.dirty) void this.save();
		this.setMyLine(null, true);
	}

	/** Gateway: somebody (maybe us) saved. */
	applyRemote(payload: ChannelPadPayload): void {
		if (payload.locks) this.locks = payload.locks;
		// Even while we type, the log has a new entry and the dot should show.
		if (payload.version > this.version) this.noteSaver(payload);
		if (payload.client_id && payload.client_id === this.clientId) {
			// Our own save echoed back; the save handler already moved the base forward.
			return;
		}
		if (payload.version <= this.version && this.loaded) return;
		if (this.dirty) {
			// Keep typing; the pending save will be merged against this newer version by the server.
			return;
		}
		this.accept(payload);
		this.loaded = true;
		this.status = 'saved';
	}

	applyLocks(locks: Array<ChannelPadLock>): void {
		this.locks = locks;
	}

	/** Tells the others which line the caret is on; throttled, and only when it changes. */
	setMyLine(line: string | null, force = false): void {
		const now = Date.now();
		if (!force && line === this.myLine && now - this.lockSentAt < LOCK_REFRESH_MS) return;
		if (!force && line === this.myLine && line === null) return;
		this.myLine = line;
		this.lockSentAt = now;
		http
			.post(Endpoints.KONVO_CHANNEL_SCRATCHPAD_LOCKS(this.channelId), {body: {line}, mode: 'silent'})
			.catch(() => undefined);
	}

	private scheduleSave(): void {
		if (this.saveTimer != null) clearTimeout(this.saveTimer);
		this.saveTimer = setTimeout(() => {
			this.saveTimer = null;
			void this.save();
		}, SAVE_DEBOUNCE_MS);
	}

	private async save(): Promise<void> {
		if (this.inFlight) {
			this.scheduleSave();
			return;
		}
		const snapshot = this.content;
		const run = (async () => {
			try {
				const response = await http.put<ChannelPadPayload>(Endpoints.KONVO_CHANNEL_SCRATCHPAD(this.channelId), {
					body: {
						content: snapshot,
						base_version: this.baseVersion,
						base_content: this.baseContent,
						client_id: this.clientId,
					},
					mode: 'silent',
				});
				const body = response.body;
				if (response.status === 409 && body?.content != null) {
					// The same line changed under us: the other person's version wins; ours is in the undo stack.
					runInAction(() => {
						this.accept(body);
						this.dirty = false;
						this.status = 'conflict';
					});
					setTimeout(
						() =>
							runInAction(() => {
								if (this.status === 'conflict') this.status = 'saved';
							}),
						4000,
					);
					return;
				}
				if (!response.ok || !body || typeof body.version !== 'number') {
					throw new Error(`save failed with ${response.status}`);
				}
				runInAction(() => {
					this.version = body.version;
					this.noteSaver(body);
					this.baseVersion = body.version;
					if (body.merged) {
						// Someone else's lines came in. Fold them into whatever was typed since the
						// snapshot, so the next save does not look like we deleted their lines.
						this.baseContent = body.content;
						if (this.content === snapshot) {
							this.content = body.content;
							this.dirty = false;
							this.status = 'saved';
						} else {
							const local = mergeLines(snapshot, this.content, body.content);
							this.content = local.conflict ? body.content : local.text;
							this.scheduleSave();
						}
						return;
					}
					this.baseContent = snapshot;
					if (this.content === snapshot) {
						this.dirty = false;
						this.status = 'saved';
					}
				});
				if (this.content !== snapshot && !body?.merged) this.scheduleSave();
			} catch (error) {
				logger.warn('Failed to save channel scratchpad', error);
				runInAction(() => {
					this.status = 'offline';
				});
				if (this.retryTimer != null) clearTimeout(this.retryTimer);
				this.retryTimer = setTimeout(() => {
					this.retryTimer = null;
					if (this.dirty) void this.save();
				}, RETRY_MS);
			}
		})();
		this.inFlight = run;
		try {
			await run;
		} finally {
			if (this.inFlight === run) this.inFlight = null;
		}
	}
}

class KonvoChannelScratchpadStore {
	readonly pads = new Map<string, ChannelPad>();

	constructor() {
		makeAutoObservable(this, {pads: false}, {autoBind: true});
	}

	get(channelId: string): ChannelPad {
		let pad = this.pads.get(channelId);
		if (!pad) {
			pad = new ChannelPad(channelId);
			this.pads.set(channelId, pad);
		}
		return pad;
	}

	peek(channelId: string): ChannelPad | undefined {
		return this.pads.get(channelId);
	}
}

export default new KonvoChannelScratchpadStore();
